/**
 * Where an Event in a Box event CTA's voucher can be used (migration 2026-11-02-1540, owner 2026-11-02).
 * The server decides everything: whether the choice is offered (gun show / range night voucher CTA in a state with an
 * Event in a Box voucher, no fixed end date), who may change it, and what each scope means at redemption.
 */
import { supabase } from "./supabase";

export type VoucherScope = "event" | "state" | "national";

export interface VoucherScopeOptions {
  offered: boolean;
  reason: string | null;
  /** Why the choice is not offered, in words for the operator. */
  words?: string;
  scope?: VoucherScope;
  state?: string;
  state_name?: string;
  can_change?: boolean;
}

export async function getVoucherScopeOptions(
  ctaId: string,
): Promise<VoucherScopeOptions> {
  const { data, error } = await supabase.rpc(
    "event_cta_voucher_scope_options",
    { p_cta_id: ctaId },
  );
  if (error) {
    throw error;
  }
  return (data ?? {
    offered: false,
    reason: "not_found",
  }) as VoucherScopeOptions;
}

export async function setVoucherScope(
  ctaId: string,
  scope: VoucherScope,
): Promise<void> {
  const { error } = await supabase.rpc("set_event_cta_voucher_scope", {
    p_cta_id: ctaId,
    p_scope: scope,
  });
  if (error) {
    // "VOUCHER_SCOPE: <reason>" -> the reason
    throw new Error(
      (
        error.message || "Could not change where the voucher can be used"
      ).replace(/^VOUCHER_SCOPE:\s*/, ""),
    );
  }
}

/**
 * The three choices, worded for the event. "This event only" is exactly today's behaviour, which differs by type
 * (internal audit P2): a gun show voucher is not tied to a store today (it is redeemed at the show), a range night
 * voucher is good at the venue.
 */
export function voucherScopeChoices(
  stateName: string,
  eventType?: string | null,
  venueName?: string | null,
): {
  value: VoucherScope;
  label: string;
  help: string;
}[] {
  const eventOnlyHelp =
    eventType === "gun_show"
      ? "Works like today's gun show voucher."
      : venueName
        ? `Good at ${venueName}, as today.`
        : "Good at the venue, as today.";
  return [
    {
      value: "event",
      label: "This event only",
      help: eventOnlyHelp,
    },
    {
      value: "state",
      label: `Anywhere in ${stateName}`,
      help: `Good for 30 days at any participating U.S. LawShield retailer or range in ${stateName} on the locator page, and at the show while it runs.`,
    },
    {
      value: "national",
      label: "Anywhere U.S. LawShield serves",
      help: "Good for 30 days at any participating U.S. LawShield retailer or range on the locator page, except in insurance states, and at the show while it runs.",
    },
  ];
}
